import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { MdPhotoLibrary } from "react-icons/md";
import { getMediaUrl } from "../../lib/utils";

const Slider = ({ post, className = "" }) => {
  const images =
    post
      ?.split(",")
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showViewer, setShowViewer] = useState(false);

  const openImage = (index = 0) => {
    setCurrentIndex(index);
    setShowViewer(true);
  };

  const closeViewer = () => {
    setShowViewer(false);
  };

  const nextImage = (e) => {
    e?.stopPropagation();

    if (images.length <= 1) return;

    setCurrentIndex((index) => (index + 1) % images.length);
  };

  const prevImage = (e) => {
    e?.stopPropagation();

    if (images.length <= 1) return;

    setCurrentIndex(
      (index) => (index - 1 + images.length) % images.length
    );
  };

  useEffect(() => {
    if (!showViewer) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowViewer(false);
      }

      if (e.key === "ArrowRight" && images.length > 1) {
        setCurrentIndex((index) => (index + 1) % images.length);
      }

      if (e.key === "ArrowLeft" && images.length > 1) {
        setCurrentIndex(
          (index) => (index - 1 + images.length) % images.length
        );
      }
    };

    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showViewer, images.length]);

  if (!images.length) return null;

  return (
    <>
      {/* ================= FEED IMAGE ================= */}

      <div
        className={`
          relative
          w-full
          overflow-hidden
          ${className}
        `}
      >
        <button
          type="button"
          onClick={() => openImage(0)}
          className="
            block
            w-full
            overflow-hidden
          "
        >
          <img
            src={getMediaUrl(images[0])}
            alt="Post"
            loading="lazy"
            className="
              block
              h-auto
              max-h-[700px]
              w-full
              object-contain
              object-center
            "
          />
        </button>

        {/* ================= IMAGE COUNT ================= */}

        {images.length > 1 && (
          <div
            className="
              pointer-events-none
              absolute
              right-3
              top-3
              z-20
              flex
              items-center
              gap-1.5
              rounded-full
              bg-black/70
              px-3
              py-1.5
              text-sm
              font-semibold
              text-white
              shadow-lg
              backdrop-blur-sm
            "
          >
            <MdPhotoLibrary size={18} />

            <span>{images.length}</span>
          </div>
        )}
      </div>

      {/* ================= FULLSCREEN IMAGE VIEWER ================= */}

      {showViewer && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95"
          onClick={closeViewer}
        >
          {/* ================= CLOSE ================= */}

          <button
            type="button"
            onClick={closeViewer}
            aria-label="Close image viewer"
            className="absolute right-4 top-4 z-[10001] flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-3xl leading-none text-white shadow-lg backdrop-blur-sm transition hover:bg-black/80 sm:right-6 sm:top-6"
          >
            ×
          </button>

          {/* ================= COUNTER ================= */}

          {images.length > 1 && (
            <div className="absolute left-1/2 top-4 z-[10001] flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-sm sm:top-6">
              <MdPhotoLibrary size={17} />

              <span>
                {currentIndex + 1} / {images.length}
              </span>
            </div>
          )}

          {/* ================= PREVIOUS ================= */}

          {images.length > 1 && (
            <button
              type="button"
              onClick={prevImage}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 z-[10001] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm transition hover:bg-black/80 sm:left-6 sm:h-12 sm:w-12"
            >
              <FaArrowLeft size={18} />
            </button>
          )}

          {/* ================= CURRENT IMAGE ================= */}

          <div
            className="flex h-full w-full items-center justify-center px-4 py-16 sm:px-20 sm:py-20"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getMediaUrl(images[currentIndex])}
              alt={`Post ${currentIndex + 1}`}
              className="block max-h-full max-w-full object-contain"
            />
          </div>

          {/* ================= NEXT ================= */}

          {images.length > 1 && (
            <button
              type="button"
              onClick={nextImage}
              aria-label="Next image"
              className="absolute right-2 top-1/2 z-[10001] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm transition hover:bg-black/80 sm:right-6 sm:h-12 sm:w-12"
            >
              <FaArrowRight size={18} />
            </button>
          )}

          {/* ================= MOBILE COUNTER ================= */}

          {images.length > 1 && (
            <div className="absolute bottom-5 left-1/2 z-[10001] -translate-x-1/2 rounded-full bg-black/60 px-4 py-1.5 text-xs font-medium text-white backdrop-blur-sm sm:hidden">
              {currentIndex + 1} of {images.length}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Slider;